<template>
  <b-modal :visible="true" title="Фактически выполненные работы" size="lg" centered hide-footer
    :no-close-on-backdrop="saving" :no-close-on-esc="saving" :hide-header-close="saving" @hide="onHide">
    <form novalidate class="work-form" @submit.prevent="confirm">
      <div class="work-tabs" role="tablist" aria-label="Данные смены">
        <button v-for="tab in tabs" :key="tab.id" type="button" role="tab" :aria-selected="activeTab === tab.id"
          :class="{active: activeTab === tab.id}" @click="activeTab = tab.id">{{ tab.label }}</button>
      </div>
      <p class="work-performer">{{ entry.worker_name }} · Заявка №{{ entry.task_id }}</p>
      <b-alert :show="!!error || !!validationError" variant="warning">{{ validationError || error }}</b-alert>
      <div v-show="activeTab === 'work'" class="work-columns">
        <section v-for="side in ['plan', 'actual']" :key="side" class="work-panel" :aria-label="side === 'plan' ? 'Запланированные работы' : 'Выполненные работы'">
          <h5>{{ side === 'plan' ? 'Запланированные работы' : 'Выполненные работы' }}</h5>
          <label>Время начала
            <input v-model="values[side].start" type="datetime-local" :disabled="side === 'plan' || saving" step="60" required>
          </label>
          <label>Время окончания
            <input v-model="values[side].end" type="datetime-local" :disabled="side === 'plan' || saving" step="60" required>
          </label>
          <label>Формат выхода<input :value="employmentLabel" disabled></label>
          <label>Вид работ<input :value="entry.work_name || 'Работы по заявке'" disabled></label>
          <label>Кол-во
            <span class="work-quantity">
              <span class="work-input-unit"><input v-model.number="values[side].total_units" type="number" min="0" max="1000000000" step="any" :disabled="side === 'plan' || saving" required><span>{{ unitLabel }}</span></span>
              <input :value="formatNumber(values[side].price) + ' ₽/' + unitLabel" disabled aria-label="Ставка">
            </span>
          </label>
          <div v-for="service in values[side].services" :key="service.id" class="work-service">
            <label>Услуги исполнителя<input :value="service.name" disabled></label>
            <label>Кол-во
              <span class="work-quantity">
                <span class="work-input-unit"><input v-model.number="service.quantity" type="number" min="0" max="1000000000" step="any" :disabled="side === 'plan' || saving" required><span>{{ service.unit_name || unitLabel }}</span></span>
                <input :value="formatNumber(service.price) + ' ₽'" disabled aria-label="Ставка услуги">
              </span>
            </label>
          </div>
        </section>
      </div>
      <section v-show="activeTab === 'expenses'" class="work-panel work-expenses" aria-label="Расходы по исполнителю">
        <label v-for="expense in expenses" :key="expense.key">{{ expense.label }}
          <span class="work-input-unit"><input v-model.number="values.actual[expense.key]" type="number" min="0" max="1000000000" step="0.01" placeholder="Сумма" :disabled="saving" required><span>₽</span></span>
        </label>
        <p>Обед, трансфер и другие расходы включаются в сумму с заказчика. Аванс вычитается из остатка выплаты исполнителю.</p>
      </section>
      <div class="work-footer">
        <button type="button" class="custom-btn-secondary" :disabled="saving" @click="$emit('close')">Отменить</button>
        <button type="submit" class="custom-btn-primary" :disabled="saving">{{ saving ? 'Сохранение…' : 'Подтвердить' }}</button>
      </div>
    </form>
  </b-modal>
</template>
<script>
import unitStore from '../../utilites/unitStore';

// The schedule and date grouping in this product use Moscow time, independently
// of the browser's local zone. Include that offset when saving actual work.
function moscowInput(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : new Date(date.getTime() + 3 * 3600000).toISOString().slice(0, 16);
}
function workValues(work) {
  return {...work, start: moscowInput(work.start_date), end: moscowInput(work.end_date),
    services: (work.services || []).map(service => ({...service})),
    road_price: Number(work.road_price || 0), prepaid: Number(work.prepaid || 0),
    meal_cost: Number(work.meal_cost || 0), other_cost: Number(work.other_cost || 0)};
}
export default {
  name: 'TaskTimesheetWorkModal',
  mixins: [unitStore],
  props: {entry: {type: Object, required: true}, saving: Boolean, error: String},
  data() {
    return {activeTab: 'work', validationError: '', values: {plan: workValues(this.entry.plan || this.entry), actual: workValues(this.entry)},
      tabs: [{id: 'work', label: 'Выполненные работы'}, {id: 'expenses', label: 'Расходы'}],
      expenses: [{key: 'meal_cost', label: 'Обед'}, {key: 'road_price', label: 'Трансфер'}, {key: 'prepaid', label: 'Аванс'}, {key: 'other_cost', label: 'Другое'}]};
  },
  computed: {
    employmentLabel() { return {1: 'СМЗ', 2: 'ГПХ', 3: 'Физическое лицо', 4: 'ИП', 5: 'В штате'}[this.entry.employment_type] || 'Не указан'; },
    unitLabel() { const unit = this.getUnitByCode(this.entry.unit_code); return unit && unit.shortName || 'ед.'; },
  },
  methods: {
    formatNumber(value) { return Number(value || 0).toLocaleString('ru-RU', {maximumFractionDigits: 2}); },
    onHide(event) { if (this.saving) event.preventDefault(); else this.$emit('close'); },
    confirm() {
      if (this.saving) return;
      const actual = this.values.actual;
      const start = new Date(actual.start + ':00+03:00'), end = new Date(actual.end + ':00+03:00');
      const fields = ['total_units', 'road_price', 'prepaid', 'meal_cost', 'other_cost'];
      const valid = value => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1e9;
      if (!fields.every(key => valid(actual[key])) || !actual.services.every(service => valid(service.quantity))) {
        this.validationError = 'Укажите неотрицательные количества и суммы.'; return;
      }
      if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end < start) {
        this.validationError = 'Проверьте даты: окончание смены не может быть раньше её начала.'; this.activeTab = 'work'; return;
      }
      this.validationError = '';
      this.$emit('save', {id: this.entry.id, ...fields.reduce((data, key) => ({...data, [key]: actual[key]}), {}),
        start_date: start.toISOString(), end_date: end.toISOString(), services: actual.services.map(service => ({id: service.id, quantity: service.quantity}))});
    },
  },
};
</script>
<style scoped>
.work-form { color: #303540; }
.work-tabs { display: flex; gap: 24px; margin-bottom: 16px; border-bottom: 1px solid #eee; }
.work-tabs button { background: transparent; border: 0; border-bottom: 3px solid transparent; padding: 8px 0 12px; color: #555; }
.work-tabs button.active { color: #15956e; border-bottom-color: #15956e; }
.work-performer { color: #777; font-size: 13px; }
.work-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.work-panel { padding: 24px; background: #f5f5f6; border-radius: 12px; }
.work-panel h5 { font-size: 18px; font-weight: 600; margin: 0 0 24px; }
.work-panel label { display: block; margin: 0 0 16px; font-size: 14px; font-weight: 600; }
.work-panel input { display: block; width: 100%; min-width: 0; margin-top: 8px; border: 1px solid transparent; background: #fff; color: #303540; border-radius: 8px; padding: 10px 12px; font-weight: 400; }
.work-panel input:disabled { background: #eaebed; color: #92979c; }
.work-panel input:focus { outline: 2px solid #15956e; }
.work-quantity { display: grid; grid-template-columns: 3fr 2fr; gap: 8px; }
.work-input-unit { position: relative; display: block; min-width: 0; }
.work-input-unit input { padding-right: 46px; }
.work-input-unit > span { position: absolute; right: 12px; top: 12px; font-weight: 400; pointer-events: none; }
.work-expenses { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
.work-expenses p { grid-column: 1 / -1; font-size: 12px; color: #777; margin-bottom: 0; }
.work-footer { display: flex; justify-content: flex-end; gap: 12px; padding-top: 24px; margin-top: 24px; border-top: 1px solid #eee; }
@media(max-width: 575px) { .work-columns { grid-template-columns: 1fr; } .work-expenses { gap: 8px 12px; padding: 16px; } .work-panel { padding: 16px; } }
</style>



// WEBPACK FOOTER //
// src/components/Tasks/TaskTimesheetWorkModal.vue